import React, { useState, useEffect } from "react";
import {
    Button,
    Dropdown,
    Input,
    Option,
    makeStyles,
} from "@fluentui/react-components";
import {
    Add20Regular,Delete20Regular
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

import QueueSelectorComponent from "../GenericComponent/QueueSelectorComponent";
import CustomerChoiceChatComponent from "./Routing/CustomerChoiceChatComponent";
import FormChatComponent from "./Routing/FormChatComponent";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import AddRoutingDialog from "./ChatFlowDiagram/AddRoutingDialog.js";
import "../CSS/RoutingComponent.css";

const useStyles = makeStyles({
    container: {
        display: "flex",
        gap: "1rem",
    },
    leftPanel: {
        width: "40%",
        borderRight: "1px solid #ccc",
        paddingRight: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        maxHeight: "70vh",
        overflowY: "auto",
    },
    routingItem: {
        padding: "0.5rem",
        cursor: "pointer",
        borderRadius: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "0.5rem",
        "&:hover": { backgroundColor: "#eee" },
    },
    selectedRouting: {
        backgroundColor: "#ddd",
       
    },
    rightPanel: {
        width: "60%",
        paddingLeft: "1rem",
    },
    addRow: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        marginBottom: "1rem",
    },
    editingTitle: {
        fontWeight: 600,
        fontSize: "1.2rem",
        marginBottom: "1rem",
    },
});

const ROUTING_TYPES = [
    "Queue",
    "Customer Choice",
    "Form",
];

export default function RoutingChatComponent({ chatFlowData, setChatFlowData, ACTIVE_TENANT, selectedRouting = null, permissions, isEditMode, isInDiagramPanel = false }) {
    const { t } = useTranslation();
    const classes = useStyles();

    const [routingToDelete, setRoutingToDelete] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const routingMap = chatFlowData.routing || {};

    const [selectedRoutingName, setSelectedRoutingName] = useState(() => {
        const initial = (() => {
            if (selectedRouting && routingMap[selectedRouting]) return selectedRouting;
            const routingKeys = Object.keys(routingMap);
            if (routingKeys.includes("0")) return "0";
            return routingKeys[0] || null;
        })();
        return initial;
    });

    useEffect(() => {
        if (selectedRouting && routingMap[selectedRouting]) {
            setSelectedRoutingName(selectedRouting);
        }
    }, [selectedRouting, routingMap]);

    const currentRouting = routingMap[selectedRoutingName] || {};
    

    const addNewRouting = (routingName) => {
        const updated = {
            ...routingMap,
            [routingName]: {
                enabled: true,
                displayName: routingName,
                type: "Queue",
                queueDisplayName: "",
                queueId: "",
                timeout: 10,
                message: { type: "text", language: "en-US", text: "" },
                options: {},
                next: { type: "" },
            },
        };
        setChatFlowData((prev) => ({ ...prev, routing: updated }));
        setSelectedRoutingName(routingName);
    };

    const deleteRouting = (name) => {
        const updated = { ...routingMap };
        delete updated[name];
        setChatFlowData((prev) => ({ ...prev, routing: updated }));
        if (selectedRoutingName === name) {
            const keys = Object.keys(updated);
            setSelectedRoutingName(keys[0] || null);
        }
        setRoutingToDelete(null);
    };

    const updateRouting = (field, value) => {
        const updated = {
            ...routingMap,
            [selectedRoutingName]: {
                ...currentRouting,
                [field]: value,
            },
        };
        setChatFlowData((prev) => ({ ...prev, routing: updated }));
    };

    // Dans le panneau de diagramme, toujours permettre l'édition
    const canEdit = isInDiagramPanel 
        ? true 
        : (isEditMode ? permissions?.routing?.canUpdate : permissions?.routing?.canCreate);

    const canDelete = isInDiagramPanel ? true : permissions?.routing?.canDelete;


    const renderRoutingEditor = () => {
        const type = currentRouting.type;

        switch (type) {
            case "Queue":
            case "queue":
                return (
                    <div>
                        <h4>{t("QUEUE")}</h4>
                        <QueueSelectorComponent
                            displayName={currentRouting.queueDisplayName}
                            getData={(queueData) => {
                                const updated = {
                                    ...routingMap,
                                    [selectedRoutingName]: {
                                        ...currentRouting,
                                        queueId: queueData.id,
                                        queueDisplayName: queueData.displayName,
                                    },
                                };
                                setChatFlowData((prev) => ({ ...prev, routing: updated }));
                            }}
                            ACTIVE_TENANT={ACTIVE_TENANT}
                        />
                    </div>
                );
            case "Customer Choice":
            case "customer choice":
            case "customerChoice":
                return (
                    <CustomerChoiceChatComponent
                        routing={{ ...currentRouting, routingName: selectedRoutingName }}
                        updateRouting={(updatedRouting) => {
                            const updated = { ...routingMap, [selectedRoutingName]: updatedRouting };
                            setChatFlowData((prev) => ({ ...prev, routing: updated }));
                        }}
                        chatFlowData={chatFlowData}
                    />
                );
            case "Form":
            case "form":
                return (
                    <FormChatComponent
                        routing={{ ...currentRouting, routingName: selectedRoutingName }}
                        updateRouting={(updatedRouting) => {
                            const updated = { ...routingMap, [selectedRoutingName]: updatedRouting };
                            setChatFlowData((prev) => ({ ...prev, routing: updated }));
                        }}
                        chatFlowData={chatFlowData}
                    />
                );
            default:
                console.warn('⚠️ Type de routing non reconnu:', type);
                return <div>Type de routing non pris en charge: {type}</div>;
        }
    };

    return (
        <div className={classes.container}>
            {!isInDiagramPanel && (
            <div className={classes.leftPanel}>
                <div className={classes.addRow}>
                    <Button 
                        appearance="primary" 
                        icon={<Add20Regular />} 
                        onClick={() => setIsDialogOpen(true)} 
                        disabled={!canEdit}
                    >
                    </Button>
                </div>

                {Object.keys(routingMap).map((name) => (
                    <div
                        key={name}
                        className={`${classes.routingItem} ${selectedRoutingName === name ? classes.selectedRouting : ""}`}
                        onClick={() => setSelectedRoutingName(name)}
                    >
                        <span>{name === "0" ? t("START") : (routingMap[name].displayName || name)}</span>
                        {name !== "0" && (
                            <Button
                                size="small"
                                 icon={<Delete20Regular />}
                                appearance="subtle"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setRoutingToDelete(name);
                                }}
                                disabled={!canDelete}
                            >
                               
                            </Button>
                        )}
                    </div>
                ))}
            </div>
            )}

            <div className={classes.rightPanel} style={isInDiagramPanel ? { width: "100%", paddingLeft: 0 } : {}}>
                {selectedRoutingName && currentRouting ? (
                    <>
                        <div className={classes.editingTitle}>
                            {t("EDITING")}: {selectedRoutingName === "0" ? t("START") : (currentRouting.displayName || selectedRoutingName)}
                        </div>

                        <div style={{ marginBottom: "0.5rem", display: "flex", gap: "1rem" }}>
                            <div style={{ flex: 1 }}>
                                <label>{t("DISPLAY_NAME")}</label>
                                <Input
                                    className="routing-dropdown-type"
                                    value={currentRouting.displayName || ""}
                                    onChange={(e) => updateRouting("displayName", e.target.value)}
                                    disabled={!canEdit}
                                    placeholder={selectedRoutingName === "0" ? "Start Routing" : ""}
                                />
                            </div>

                            <div style={{ flex: 1 }}>
                                <label style={{marginBottom: "0.5rem"}}>{t("TYPE")}</label>
                                <Dropdown
                                className="routing-dropdown-type"
                                 
                                    value={currentRouting.type || "Queue"}
                                    selectedOptions={[currentRouting.type || "Queue"]}
                                    onOptionSelect={(_, data) => updateRouting("type", data.optionValue)}
                                    disabled={!canEdit}
                                >
                                    {ROUTING_TYPES.map((type) => (
                                        <Option key={type} value={type}>
                                            {t(type)}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                        </div>

                        {renderRoutingEditor()}
                    </>
                ) : (
                    <p>{t("SELECT_OR_CREATE_ROUTING")}</p>
                )}
            </div>

            {routingToDelete && (
                <ConfirmAlert
                    open={!!routingToDelete}
                    name={routingMap[routingToDelete]?.displayName || routingToDelete}
                    onConfirm={() => deleteRouting(routingToDelete)}
                    onCancel={() => setRoutingToDelete(null)}
                />
            )}

            <AddRoutingDialog
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onAdd={addNewRouting}
                existingRoutingNames={Object.keys(routingMap)}
            />
        </div>
    );
}
